/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminPrimaryBillingPointShow from 'src/components/Admin/AdminPrimaryBillingPoint/AdminPrimaryBillingPointShow';
import AdminShowPlaceholder from 'src/components/Admin/AdminShowPlaceholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getShowRouteKey from './helpers/getShowRouteKey';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading this primary billing point. Please try again.',
  },
  notFoundTitle: {
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.page.not_found.title',
    defaultMessage: 'Primary billing point not found',
  },
});

export const adminPrimaryBillingPointShowRouteQuery = graphql`
  query AdminPrimaryBillingPointShowRoute_Query($uuid: ID!) {
    primaryBillingPoint(uuid: $uuid) {
      ...AdminPrimaryBillingPointShow_primaryBillingPoint
    }
  }
`;

/**
 * Renders `AdminPrimaryBillingPointShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin primary billing point show route content.
 */
function AdminPrimaryBillingPointShowPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminPrimaryBillingPointShowRouteQuery, queryRef);

  return <AdminPrimaryBillingPointShow {...routeProps} {...queryData} />;
}

AdminPrimaryBillingPointShowPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminPrimaryBillingPointShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin primary billing point show route content.
 */
function AdminPrimaryBillingPointShowRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminPrimaryBillingPointShowRouteQuery,
    routeProps,
    { logLabel: 'AdminPrimaryBillingPointShowRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      notFound={{ title: intl.formatMessage(messages.notFoundTitle), backPath: '/admin/primary-billing-points' }}
      placeholderComponent={AdminShowPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminPrimaryBillingPointShowPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminPrimaryBillingPointShow {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminPrimaryBillingPointShowRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the AdminPrimaryBillingPointShow route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. The renderer is keyed with
 * `getShowRouteKey` (uuid and non-locale query params), so navigating to
 * another primary billing point or query string remounts it and drops a
 * retried query reference that would otherwise hide a later route error.
 * The key deliberately leaves out the location key: the meters table links
 * back to this page, and found-relay reuses the route query for a same-URL
 * navigation without refetching, so a remount there would only replay the
 * error the user already retried. Authentication is resolved by the owning
 * `AdminLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} AdminPrimaryBillingPointShow route content or loading state.
 */
export function adminPrimaryBillingPointShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <AdminPrimaryBillingPointShowRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <AdminPrimaryBillingPointShowRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default AdminPrimaryBillingPointShowRouteRenderer;
